import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getEarnings, fmtPrice } from "@/lib/api";
import { CalendarClock, Zap, TrendingUp, TrendingDown, ArrowRight, Flame } from "lucide-react";

const dayLabel = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
};

export const EarningsPanel = ({ onSelect }) => {
  const { data, isLoading } = useQuery({ queryKey: ["earnings"], queryFn: getEarnings, refetchInterval: 60000 });
  const rows = data?.earnings || [];

  return (
    <div className="max-w-3xl" data-testid="earnings-panel">
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-app/60">
          <div className="flex items-center gap-2">
            <CalendarClock size={16} className="text-ai" />
            <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Earnings Radar · This Week</h3>
          </div>
          {data && (
            <span className="text-[9px] font-mono uppercase text-tertiary">
              {data.source === "finnhub" ? "Live · Finnhub" : "Simulated"}
            </span>
          )}
        </div>

        <div className="px-4 py-2.5 border-b border-border-subtle bg-app/30">
          <p className="text-[11px] text-tertiary font-mono">
            IV ≥ {Math.round((data?.iv_threshold || 0.5) * 100)}% flags an elevated-volatility name — a <span className="text-warn">Long Straddle</span> profits from a big post-earnings move either way.
          </p>
        </div>

        {isLoading && <div className="p-8 text-center text-tertiary font-mono text-xs">Loading earnings calendar…</div>}
        {!isLoading && !rows.length && (
          <div className="p-8 text-center text-tertiary font-mono text-xs">No tracked tickers report this week.</div>
        )}

        {rows.map((e) => (
          <div key={e.symbol} data-testid={`earnings-${e.symbol}`}
            className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle hover:bg-surface-2 transition-colors">
            <div className="w-24 shrink-0">
              <div className="font-mono font-bold text-white">{e.symbol}</div>
              <div className="text-[10px] text-tertiary truncate">{e.name}</div>
            </div>
            <div className="w-28 shrink-0">
              <div className="text-xs font-mono text-white">{dayLabel(e.date)}</div>
              <div className="text-[9px] font-mono uppercase text-tertiary">
                {e.hour === "bmo" ? "Before open" : e.hour === "amc" ? "After close" : "—"}
              </div>
            </div>
            <div className="text-right w-20 shrink-0">
              <div className="font-mono text-sm text-white">${fmtPrice(e.price)}</div>
              <div className={`text-[10px] font-mono ${e.change_pct >= 0 ? "text-bull" : "text-bear"}`}>
                {e.change_pct >= 0 ? "+" : ""}{e.change_pct}%
              </div>
            </div>
            <div className="flex-1 flex items-center gap-2 justify-end">
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${e.iv_elevated ? "bg-warn/10 text-warn border-warn/25" : "bg-surface-2 text-tertiary border-border"}`}>
                IV {Math.round(e.iv * 100)}%
              </span>
              {e.iv_elevated && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-warn/10 text-warn border-warn/25 flex items-center gap-1">
                  <Flame size={10} /> Elevated
                </span>
              )}
              <button data-testid={`straddle-${e.symbol}`} onClick={() => onSelect(e.symbol)}
                className={`text-[11px] font-mono px-2.5 py-1.5 rounded border flex items-center gap-1 transition-colors ${e.iv_elevated ? "bg-ai/10 text-ai border-ai/30 hover:bg-ai/20" : "bg-surface-2 text-secondary border-border hover:bg-surface-3"}`}>
                <Zap size={11} /> {e.suggest || "Analyze"} <ArrowRight size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
